import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  MagnifyingGlass,
  Funnel,
  CaretDown,
  CaretRight,
  Phone,
  Envelope,
  MapPin,
  Clock,
  User,
  CheckCircle,
  XCircle,
  Warning,
  Trash,
  Eye,
  X,
  ChartBar,
  TrendUp,
  CurrencyDollar,
  Users,
  ArrowLeft,
  NotePencil,
  ShieldCheck,
  Export,
  CalendarBlank
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';
import axiosInstance from '../api/api';

const statusColors = {
  new: { bg: 'bg-blue-100 dark:bg-blue-900/30', text: 'text-blue-700 dark:text-blue-300', border: 'border-blue-500' },
  contacted: { bg: 'bg-amber-100 dark:bg-amber-900/30', text: 'text-amber-700 dark:text-amber-300', border: 'border-amber-500' },
  converted: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', text: 'text-emerald-700 dark:text-emerald-300', border: 'border-emerald-500' },
  closed: { bg: 'bg-neutral-100 dark:bg-neutral-800', text: 'text-neutral-600 dark:text-neutral-400', border: 'border-neutral-400' }
};

const jobTypeLabels = {
  bathroom_remodel: 'Bathroom Remodel',
  kitchen_remodel: 'Kitchen Remodel',
  roofing: 'Roofing',
  painting: 'Painting',
  flooring: 'Flooring',
  electrical: 'Electrical',
  plumbing: 'Plumbing',
  hvac: 'HVAC',
  carpentry: 'Carpentry',
  concrete: 'Concrete/Masonry',
  deck_patio: 'Deck/Patio',
  windows_doors: 'Windows & Doors',
  siding: 'Siding',
  landscaping: 'Landscaping',
  general: 'General Contractor',
  other: 'Other'
};

export default function AdminLeads() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [scoreStats, setScoreStats] = useState(null);
  const [rescoring, setRescoring] = useState(false);
  const [total, setTotal] = useState(0);
  const [selectedLead, setSelectedLead] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterJobType, setFilterJobType] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [newNote, setNewNote] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const limit = 20;

  useEffect(() => {
    checkAuthorization();
  }, []);

  useEffect(() => {
    if (isAuthorized) {
      fetchLeads();
      fetchStats();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [isAuthorized, currentPage, filterStatus, filterJobType]);

  const checkAuthorization = () => {
    const userStr = localStorage.getItem('user');
    if (!userStr) {
      setAuthChecking(false);
      setIsAuthorized(false);
      return;
    }
    
    try {
      const user = JSON.parse(userStr);
      if (user.user_type === 'admin') {
        setIsAuthorized(true);
      }
    } catch (e) {
      console.error('Error parsing user:', e);
    }
    setAuthChecking(false);
  };

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('skip', currentPage * limit);
      params.append('limit', limit);
      if (filterStatus) params.append('status', filterStatus);
      if (filterJobType) params.append('job_type', filterJobType);
      if (searchQuery) params.append('search', searchQuery);

      const response = await axiosInstance.get(`/leads/admin/all?${params.toString()}`);
      if (response.data.success) {
        setLeads(response.data.leads);
        setTotal(response.data.total);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      toast({ title: "Error", description: "Failed to fetch leads", variant: "destructive" });
    }
    setLoading(false);
  };

  const fetchStats = async () => {
    try {
      const response = await axiosInstance.get('/leads/stats');
      if (response.data.success) {
        setStats(response.data.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
    try {
      const scoreRes = await axiosInstance.get('/leads/score-distribution');
      if (scoreRes.data.success) setScoreStats(scoreRes.data);
    } catch { /* ignore */ }
  };

  const handleRescoreAll = async () => {
    setRescoring(true);
    try {
      const res = await axiosInstance.post('/leads/admin/rescore-all');
      if (res.data.success) {
        toast({ title: 'Leads re-scored', description: `${res.data.updated} leads updated` });
        fetchLeads();
        fetchStats();
      }
    } catch { toast({ title: 'Error re-scoring', variant: 'destructive' }); }
    finally { setRescoring(false); }
  };

  const handleSearch = () => {
    setCurrentPage(0);
    fetchLeads();
  };

  const updateLeadStatus = async (leadId, newStatus) => {
    try {
      await axiosInstance.put(`/leads/admin/${leadId}`, { status: newStatus });
      toast({ title: "Success", description: "Lead status updated" });
      fetchLeads();
      fetchStats();
      if (selectedLead && selectedLead._id === leadId) {
        setSelectedLead({ ...selectedLead, status: newStatus });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update status", variant: "destructive" });
    }
  };

  const addNote = async (leadId) => {
    if (!newNote.trim()) return;
    try {
      await axiosInstance.put(`/leads/admin/${leadId}`, { notes: newNote });
      toast({ title: "Success", description: "Note added" });
      setNewNote('');
      // Refresh lead detail
      const response = await axiosInstance.get(`/leads/admin/${leadId}`);
      if (response.data.success) {
        setSelectedLead(response.data.lead);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to add note", variant: "destructive" });
    }
  };

  const deleteLead = async (leadId) => {
    if (!confirm('Are you sure you want to delete this lead?')) return;
    try {
      await axiosInstance.delete(`/leads/admin/${leadId}`);
      toast({ title: "Success", description: "Lead deleted" });
      setShowDetailModal(false);
      fetchLeads();
      fetchStats();
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete lead", variant: "destructive" });
    }
  };

  const openLeadDetail = async (lead) => {
    try {
      const response = await axiosInstance.get(`/leads/admin/${lead._id}`);
      if (response.data.success) {
        setSelectedLead(response.data.lead);
        setShowDetailModal(true);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to load lead details", variant: "destructive" });
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  // Access denied screen
  if (authChecking) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white dark:bg-[#0F172A] border-4 border-black shadow-[8px_8px_0px_#000] p-8 text-center">
          <ShieldCheck size={64} weight="bold" className="mx-auto mb-4 text-red-500" />
          <h1 className="text-2xl font-black uppercase mb-2">Admin Access Only</h1>
          <p className="text-neutral-600 dark:text-neutral-400 mb-6">
            This page is restricted to administrators.
          </p>
          <Button onClick={() => navigate('/')} className="w-full">Return to Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8E9EB] dark:bg-[#121212] p-4 md:p-8">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="outline"
              onClick={() => navigate('/admin')}
              className="border-2 border-black dark:border-white rounded-none"
            >
              <ArrowLeft size={18} weight="bold" className="mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-3xl font-black text-neutral-900 dark:text-white uppercase">
                Lead Management
              </h1>
              <p className="text-neutral-500">View and manage all incoming leads</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500 border-2 border-black flex items-center justify-center">
                  <Users size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.total}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Total Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-500 border-2 border-black flex items-center justify-center">
                  <Clock size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.new}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">New Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-purple-500 border-2 border-black flex items-center justify-center">
                  <Phone size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.contacted}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Contacted</p>
                </div>
              </div>
            </div>
            <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000]">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-500 border-2 border-black flex items-center justify-center">
                  <CheckCircle size={20} weight="bold" className="text-white" />
                </div>
                <div>
                  <p className="text-2xl font-black text-neutral-900 dark:text-white">{stats.converted}</p>
                  <p className="text-xs text-neutral-500 uppercase tracking-wider">Converted</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Lead Score Summary */}
        {scoreStats && (
          <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000] mb-6" data-testid="score-summary">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-black uppercase text-neutral-700 dark:text-neutral-300 flex items-center gap-2">
                <TrendUp size={16} weight="bold" className="text-[#2B4C6F]" /> Lead Score Distribution
              </h3>
              <button onClick={handleRescoreAll} disabled={rescoring}
                className="h-8 px-3 bg-[#2B4C6F] hover:bg-[#203A55] text-white text-xs font-bold border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none transition-all disabled:opacity-50 flex items-center gap-1"
                data-testid="rescore-all-btn">
                {rescoring ? <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <ChartBar size={12} weight="bold" />}
                Re-score All
              </button>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {scoreStats.distribution?.map((d, i) => (
                <div key={i} className={`px-3 py-2 border-2 text-center min-w-[60px] ${
                  d.grade === 'A' ? 'bg-emerald-100 border-emerald-400 text-emerald-700' :
                  d.grade === 'B' ? 'bg-blue-100 border-blue-400 text-blue-700' :
                  d.grade === 'C' ? 'bg-amber-100 border-amber-400 text-amber-700' :
                  d.grade === 'D' ? 'bg-orange-100 border-orange-400 text-orange-700' :
                  'bg-red-100 border-red-400 text-red-700'
                }`}>
                  <p className="text-lg font-black">{d.count}</p>
                  <p className="text-[10px] font-bold uppercase">Grade {d.grade}</p>
                </div>
              ))}
              <div className="px-3 py-2 border-2 border-[#2B4C6F] bg-[#2B4C6F]/10 text-center min-w-[80px]">
                <p className="text-lg font-black text-[#2B4C6F]">{scoreStats.average_score}</p>
                <p className="text-[10px] font-bold uppercase text-neutral-500">Avg Score</p>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white p-4 shadow-[4px_4px_0px_#000] mb-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Search</Label>
              <div className="relative">
                <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Name, email, phone, or zip..."
                  className="pl-10 border-2 border-black dark:border-white rounded-none"
                />
              </div>
            </div>
            <div className="w-40">
              <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Status</Label>
              <select
                value={filterStatus}
                onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(0); }}
                className="w-full h-10 px-3 border-2 border-black dark:border-white bg-white dark:bg-neutral-900 rounded-none"
              >
                <option value="">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
            <div className="w-48">
              <Label className="text-xs font-bold uppercase tracking-wider mb-2 block">Job Type</Label>
              <select
                value={filterJobType}
                onChange={(e) => { setFilterJobType(e.target.value); setCurrentPage(0); }}
                className="w-full h-10 px-3 border-2 border-black dark:border-white bg-white dark:bg-neutral-900 rounded-none"
              >
                <option value="">All Types</option>
                {Object.entries(jobTypeLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </div>
            <Button onClick={handleSearch} className="h-10 bg-[#2B4C6F] border-2 border-black rounded-none">
              <MagnifyingGlass size={18} weight="bold" className="mr-2" />
              Search
            </Button>
          </div>
        </div>

        {/* Leads Table */}
        <div className="bg-white dark:bg-[#0F172A] border-2 border-black dark:border-white shadow-[4px_4px_0px_#000] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-neutral-100 dark:bg-neutral-800 border-b-2 border-black dark:border-white">
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Contact</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Job Type</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Location</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Score</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center">
                      <div className="w-8 h-8 border-4 border-[#2B4C6F] border-t-transparent rounded-full animate-spin mx-auto"></div>
                    </td>
                  </tr>
                ) : leads.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-12 text-center text-neutral-500">
                      No leads found
                    </td>
                  </tr>
                ) : (
                  leads.map((lead) => (
                    <tr key={lead._id} className="border-b border-neutral-200 dark:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50">
                      <td className="px-4 py-3">
                        <p className="font-bold text-neutral-900 dark:text-white">{lead.contact?.name}</p>
                        <p className="text-xs text-neutral-500">{lead.contact?.email}</p>
                        <p className="text-xs text-neutral-500">{lead.contact?.phone}</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-neutral-900 dark:text-white">
                          {jobTypeLabels[lead.job_type] || lead.job_type}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-neutral-900 dark:text-white">
                          {lead.location?.city}, {lead.location?.state}
                        </p>
                        <p className="text-xs text-neutral-500">{lead.location?.zip_code}</p>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={lead.status}
                          onChange={(e) => updateLeadStatus(lead._id, e.target.value)}
                          className={`px-3 py-1 text-xs font-bold uppercase border-2 ${statusColors[lead.status]?.border} ${statusColors[lead.status]?.bg} ${statusColors[lead.status]?.text} rounded-none cursor-pointer`}
                        >
                          <option value="new">New</option>
                          <option value="contacted">Contacted</option>
                          <option value="converted">Converted</option>
                          <option value="closed">Closed</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        {lead.lead_score != null ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-black rounded border-2 ${
                            lead.lead_grade === 'A' ? 'bg-emerald-100 text-emerald-700 border-emerald-400' :
                            lead.lead_grade === 'B' ? 'bg-blue-100 text-blue-700 border-blue-400' :
                            lead.lead_grade === 'C' ? 'bg-amber-100 text-amber-700 border-amber-400' :
                            lead.lead_grade === 'D' ? 'bg-orange-100 text-orange-700 border-orange-400' :
                            'bg-red-100 text-red-700 border-red-400'
                          }`} data-testid={`admin-lead-score-${lead._id}`}>
                            {lead.lead_score} {lead.lead_grade}
                          </span>
                        ) : (
                          <span className="text-xs text-neutral-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-neutral-500">
                        {formatDate(lead.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openLeadDetail(lead)}
                          className="border-2 border-black dark:border-white rounded-none"
                        >
                          <Eye size={16} weight="bold" className="mr-1" />
                          View
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {total > limit && (
            <div className="p-4 border-t-2 border-black dark:border-white flex items-center justify-between">
              <p className="text-sm text-neutral-500">
                Showing {currentPage * limit + 1} - {Math.min((currentPage + 1) * limit, total)} of {total}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage === 0}
                  onClick={() => setCurrentPage(currentPage - 1)}
                  className="border-2 border-black dark:border-white rounded-none"
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={(currentPage + 1) * limit >= total}
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="border-2 border-black dark:border-white rounded-none"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lead Detail Modal */}
      {showDetailModal && selectedLead && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-[#0F172A] border-4 border-black dark:border-white shadow-[8px_8px_0px_#000] w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-4 border-b-2 border-black dark:border-white bg-[#2B4C6F] flex items-center justify-between">
              <h2 className="text-xl font-black text-white uppercase">Lead Details</h2>
              <button onClick={() => setShowDetailModal(false)} className="text-white hover:text-neutral-200">
                <X size={24} weight="bold" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Contact Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <User size={18} weight="bold" className="text-[#2B4C6F]" />
                    <span className="font-bold">{selectedLead.contact?.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Envelope size={18} weight="bold" className="text-[#2B4C6F]" />
                    <a href={`mailto:${selectedLead.contact?.email}`} className="text-[#2B4C6F] hover:underline">
                      {selectedLead.contact?.email}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone size={18} weight="bold" className="text-[#2B4C6F]" />
                    <a href={`tel:${selectedLead.contact?.phone}`} className="text-[#2B4C6F] hover:underline">
                      {selectedLead.contact?.phone}
                    </a>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin size={18} weight="bold" className="text-[#2B4C6F]" />
                    <span>{selectedLead.location?.city}, {selectedLead.location?.state} {selectedLead.location?.zip_code}</span>
                  </div>
                </div>
                <div className="space-y-3">
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Job Type</Label>
                    <p className="font-bold">{jobTypeLabels[selectedLead.job_type] || selectedLead.job_type}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Timeline</Label>
                    <p>{selectedLead.timeline?.replace('_', ' ')}</p>
                  </div>
                  <div>
                    <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</Label>
                    <select
                      value={selectedLead.status}
                      onChange={(e) => updateLeadStatus(selectedLead._id, e.target.value)}
                      className={`mt-1 px-3 py-1 text-sm font-bold border-2 ${statusColors[selectedLead.status]?.border} ${statusColors[selectedLead.status]?.bg} ${statusColors[selectedLead.status]?.text} rounded-none`}
                    >
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="converted">Converted</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  {selectedLead.lead_score != null && (
                    <div>
                      <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500">Quality Score</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 text-sm font-black border-2 ${
                          selectedLead.lead_grade === 'A' ? 'bg-emerald-100 text-emerald-700 border-emerald-400' :
                          selectedLead.lead_grade === 'B' ? 'bg-blue-100 text-blue-700 border-blue-400' :
                          selectedLead.lead_grade === 'C' ? 'bg-amber-100 text-amber-700 border-amber-400' :
                          'bg-neutral-100 text-neutral-600 border-neutral-400'
                        }`} data-testid="detail-lead-score">
                          {selectedLead.lead_score}/100 — Grade {selectedLead.lead_grade}
                        </span>
                      </div>
                      {selectedLead.score_factors && (
                        <div className="mt-2 grid grid-cols-5 gap-1 text-[10px] text-neutral-500">
                          {Object.entries(selectedLead.score_factors).map(([k, v]) => (
                            <div key={k} className="text-center">
                              <p className="font-black text-neutral-700 dark:text-neutral-300">{v}/20</p>
                              <p className="capitalize">{k.replace('_', ' ')}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Project Description</Label>
                <div className="p-4 bg-neutral-100 dark:bg-neutral-800 border-2 border-black dark:border-white">
                  <p className="text-neutral-700 dark:text-neutral-300">{selectedLead.description}</p>
                </div>
              </div>

              {/* Purchases */}
              {selectedLead.purchases && selectedLead.purchases.length > 0 && (
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">
                    Purchased By ({selectedLead.purchases.length})
                  </Label>
                  <div className="space-y-2">
                    {selectedLead.purchases.map((purchase, idx) => (
                      <div key={idx} className="p-3 bg-emerald-50 dark:bg-emerald-900/20 border-2 border-emerald-500 flex items-center justify-between">
                        <span className="text-sm">{purchase.contractor_email}</span>
                        <span className="text-xs text-neutral-500">${purchase.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-2 block">Notes</Label>
                <div className="space-y-2 mb-3 max-h-40 overflow-y-auto">
                  {selectedLead.notes?.length > 0 ? (
                    selectedLead.notes.map((note, idx) => (
                      <div key={idx} className="p-3 bg-neutral-100 dark:bg-neutral-800 border-l-4 border-[#2B4C6F]">
                        <p className="text-sm">{note.text}</p>
                        <p className="text-xs text-neutral-500 mt-1">{note.added_by} • {note.added_at}</p>
                      </div>
                    ))
                  ) : (
                    <p className="text-neutral-500 text-sm">No notes yet</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Add a note..."
                    className="border-2 border-black dark:border-white rounded-none"
                  />
                  <Button
                    onClick={() => addNote(selectedLead._id)}
                    className="bg-[#2B4C6F] border-2 border-black rounded-none"
                  >
                    <NotePencil size={18} weight="bold" />
                  </Button>
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-between pt-4 border-t-2 border-neutral-200 dark:border-neutral-700">
                <Button
                  variant="destructive"
                  onClick={() => deleteLead(selectedLead._id)}
                  className="border-2 border-black rounded-none"
                >
                  <Trash size={18} weight="bold" className="mr-2" />
                  Delete Lead
                </Button>
                <Button
                  onClick={() => setShowDetailModal(false)}
                  className="bg-[#2B4C6F] border-2 border-black rounded-none"
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
